import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Editor, Node, Transforms, Range, createEditor } from 'slate';
import { ReactEditor, Editable, Slate, withReact } from 'slate-react';
import { defineMessages, useIntl } from 'react-intl';
import { type BlockEditProps } from '@plone/types';
import config from '@plone/volto/registry';
import { P } from '@plone/volto-slate/constants';
import SidebarPortal from '@plone/volto/components/manage/Sidebar/SidebarPortal';
import { BlockDataForm } from '@plone/volto/components/manage/Form';
import './productstitle.scss';
import ProductMenu from './productmenu';
import { type ProductsTitleData } from './schema';

const messages = defineMessages({
  title: {
    id: 'Type the title…',
    defaultMessage: 'Type the title…',
  },
  overview: { id: 'overview', defaultMessage: 'Overview' },
});

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

type Props = BlockEditProps & {
  data: ProductsTitleData;
};

export const ProductsBlockEdit = (props: Props) => {
  const {
    block,
    blockNode,
    data,
    detached,
    editable,
    index,
    metadata,
    onAddBlock,
    onChangeField,
    onFocusNextBlock,
    onFocusPreviousBlock,
    onSelectBlock,
    properties,
    selected,
    blocksConfig,
    onChangeBlock,
  } = props;
  const intl = useIntl();

  const schema = blocksConfig[data['@type']].blockSchema({
    data,
    intl,
  });

  const [editor] = useState(withReact(createEditor()));
  const contentId = properties?.id || '';
  const isProduct = properties['@type'] === 'Product';
  const product_title = isProduct ? properties.title : properties.parent.title;
  const section_title = !isProduct ? product_title : null;
  const [initialValue] = useState([
    {
      type: P,
      children: [{ text: metadata?.title || properties?.title || '' }],
    },
  ]);

  const prevSelected = usePrevious(selected);

  const text = useMemo(
    () => metadata?.title || properties?.title || '',
    [metadata, properties],
  );

  const placeholder = useMemo(
    () => data.placeholder || intl.formatMessage(messages.title),
    [data.placeholder, intl],
  );
  const disableNewBlocks = useMemo(() => detached, [detached]);

  useEffect(() => {
    if (!isProduct) {
      return;
    }
    if (!prevSelected && selected) {
      if (editor.selection && Range.isCollapsed(editor.selection)) {
        // keep selection
        ReactEditor.focus(editor);
      } else {
        // nothing is selected, move focus to end
        ReactEditor.focus(editor);
        Transforms.select(editor, Editor.end(editor, []));
      }
    }
  }, [prevSelected, selected, editor, isProduct]);

  useEffect(() => {
    if (!isProduct) {
      return;
    }
    // undo/redo handlerr
    const oldText = Node.string(editor);
    if (oldText !== text) {
      Transforms.insertText(editor, text, {
        at: [0, 0],
      });
    }
  }, [editor, text, isProduct]);

  const handleChange = useCallback(() => {
    const newText = Node.string(editor);
    if (newText !== text) {
      onChangeField('title', newText);
    }
  }, [editor, onChangeField, text]);

  const handleKeyDown = useCallback(
    (ev: React.KeyboardEvent<HTMLInputElement>) => {
      if (ev.key === 'Return' || ev.key === 'Enter') {
        ev.preventDefault();
        if (!disableNewBlocks) {
          // @ts-expect-error
          onSelectBlock(
            onAddBlock(config.settings.defaultBlockType, index + 1),
          );
        }
      } else if (ev.key === 'ArrowUp') {
        ev.preventDefault();
        // @ts-expect-error
        onFocusPreviousBlock(block, blockNode.current);
      } else if (ev.key === 'ArrowDown') {
        ev.preventDefault();
        // @ts-expect-error
        onFocusNextBlock(block, blockNode.current);
      }
    },
    [
      index,
      blockNode,
      disableNewBlocks,
      onSelectBlock,
      onAddBlock,
      onFocusPreviousBlock,
      onFocusNextBlock,
      block,
    ],
  );

  const handleFocus = useCallback(() => {
    // @ts-expect-error
    onSelectBlock(block);
  }, [block, onSelectBlock]);

  const mainPageTitle = data.main_page_title
    ? data.main_page_title
    : intl.formatMessage(messages.overview);

  const getItems = (contentId, isProduct) => {
    if (!props.content?.items || props.content.items.length > 0) {
      return [
        {
          '@id': 'overview',
          id: 'overview',
          title: mainPageTitle,
          active: isProduct ? 'active' : undefined,
        },
        {
          '@id': 'components-technical-features',
          id: 'components-technical-features',
          title: 'Components & Technical Features',
          active:
            contentId === 'components-technical-features'
              ? 'active'
              : undefined,
        },
        {
          '@id': 'product-ecosystems',
          id: 'product-ecosystems',
          title: 'Products Ecosystem',
          active: contentId === 'product-ecosystems' ? 'active' : undefined,
        },
        {
          '@id': 'brochures',
          id: 'brochures',
          title: 'Brochures',
          active: contentId === 'brochures' ? 'active' : undefined,
        },
      ];
    } else {
      return [
        {
          '@id': 'overview',
          id: 'overview',
          title: mainPageTitle,
          active: isProduct ? 'active' : undefined,
        },
      ];
    }
  };

  const renderElement = useCallback(
    ({ attributes, children }) => {
      return (
        <div className="product-title-wrapper">
          <div className="product-title">
            <h1 {...attributes} className="documentFirstHeading">
              {children}
            </h1>
          </div>
          <div className="product-navigation" contentEditable="plaintext-only">
            <ProductMenu
              items={getItems(contentId, isProduct)}
              isEditMode={true}
            />
          </div>
        </div>
      );
    },
    [contentId, isProduct],
  );

  if (typeof window.__SERVER__ !== 'undefined') {
    return <div />;
  }
  const titleTag = isProduct ? (
    <h1>{product_title}</h1>
  ) : (
    <>
      <p className="h1">{product_title}</p>
      <h1 className="sr-only">{section_title}</h1>
    </>
  );
  return (
    <>
      {!isProduct && (
        <div
          className={`product-title-wrapper ${isProduct ? 'product' : 'section'}`}
        >
          <div className="product-title">{titleTag}</div>
          <div className="product-navigation">
            <ProductMenu
              items={getItems(contentId, isProduct)}
              isEditMode={true}
            />
          </div>
        </div>
      )}
      {isProduct && (
        <Slate
          editor={editor}
          onChange={handleChange}
          initialValue={initialValue}
        >
          <Editable
            readOnly={!editable}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            renderElement={renderElement}
            onFocus={handleFocus}
            aria-multiline="false"
          ></Editable>
        </Slate>
      )}
      {/* @ts-expect-error TODO fix */}
      <SidebarPortal selected={selected}>
        <BlockDataForm
          schema={schema}
          title={schema.title}
          onChangeField={(id, value) => {
            onChangeBlock(block, {
              ...data,
              [id]: value,
            });
          }}
          formData={data}
          block={block}
        />
      </SidebarPortal>
    </>
  );
};

export default ProductsBlockEdit;
